import { useEffect, useRef, useState } from "react";

interface Props {
  scenario: "baseline" | "sentiment" | "ai_trading" | "ai_trading_sentiment";
  currentFlowStep: number;
  onRevealComplete?: () => void;
  /** If true, skip the typewriter animation and show all code immediately */
  skipAnimation?: boolean;
}

const BASELINE_CODE = `from dataclasses import dataclass
from enum import Enum, auto
from typing import Optional


class SignalState(Enum):
    NEUTRAL = auto()
    BULLISH_CROSSOVER = auto()
    BEARISH_CROSSUNDER = auto()


@dataclass
class StrategyParameters:
    short_period: int = 10
    long_period: int = 30
    enable_diagnostics: bool = True
    enforce_warmup_guard: bool = True


@dataclass
class IndicatorSnapshot:
    short_ema_prev: float
    short_ema_curr: float
    long_ema_prev: float
    long_ema_curr: float

    @property
    def spread_curr(self) -> float:
        return self.short_ema_curr - self.long_ema_curr

    @property
    def spread_prev(self) -> float:
        return self.short_ema_prev - self.long_ema_prev


class DualMovingAverageStrategy(Strategy):
    """
    Multi-Stage Trend Transition Strategy
    -------------------------------------
    Core thesis:
        - Establish long exposure when short-horizon exponential momentum
          transitions from non-dominant to dominant relative to the long-horizon trend proxy.
        - Flatten existing long exposure when the inverse condition is observed.

    Notes:
        This implementation intentionally decomposes the crossover process into
        distinct analytical and execution phases for clarity, extensibility,
        and operational observability.
    """

    short_period: int = 10
    long_period: int = 30

    def __init__(self):
        super().__init__()
        self.params = StrategyParameters(
            short_period=self.short_period,
            long_period=self.long_period,
        )

    def on_bar(self, ctx):
        self._validate_parameter_state()

        if not self._has_sufficient_history(ctx):
            self._emit_diagnostics(
                ctx,
                signal=SignalState.NEUTRAL,
                snapshot=None,
                reason="insufficient_history",
            )
            return

        snapshot = self._build_indicator_snapshot(ctx)
        signal = self._evaluate_transition_state(snapshot)
        target_qty = self._determine_execution_quantity(ctx, signal)
        self._execute_signal(ctx, signal, target_qty)
        self._emit_diagnostics(ctx, signal=signal, snapshot=snapshot, reason="ok")

    def _validate_parameter_state(self) -> None:
        if self.params.short_period <= 0 or self.params.long_period <= 0:
            raise ValueError("EMA periods must be positive integers")
        if self.params.short_period >= self.params.long_period:
            raise ValueError("short_period must be strictly less than long_period")

    def _has_sufficient_history(self, ctx) -> bool:
        if not self.params.enforce_warmup_guard:
            return True

        required_bars = max(self.params.short_period, self.params.long_period) + 2
        return len(ctx.close) >= required_bars

    def _build_indicator_snapshot(self, ctx) -> IndicatorSnapshot:
        close = ctx.close

        short_ema_series = indicators.ema(close, self.params.short_period)
        long_ema_series = indicators.ema(close, self.params.long_period)

        return IndicatorSnapshot(
            short_ema_prev=short_ema_series[-2],
            short_ema_curr=short_ema_series[-1],
            long_ema_prev=long_ema_series[-2],
            long_ema_curr=long_ema_series[-1],
        )

    def _evaluate_transition_state(self, snapshot: IndicatorSnapshot) -> SignalState:
        bullish_transition = (
            snapshot.short_ema_curr > snapshot.long_ema_curr
            and snapshot.short_ema_prev <= snapshot.long_ema_prev
        )

        bearish_transition = (
            snapshot.short_ema_curr < snapshot.long_ema_curr
            and snapshot.short_ema_prev >= snapshot.long_ema_prev
        )

        if bullish_transition:
            return SignalState.BULLISH_CROSSOVER

        if bearish_transition:
            return SignalState.BEARISH_CROSSUNDER

        return SignalState.NEUTRAL

    def _determine_execution_quantity(self, ctx, signal: SignalState) -> int:
        if signal == SignalState.BULLISH_CROSSOVER:
            return ctx.max_position

        if signal == SignalState.BEARISH_CROSSUNDER:
            return ctx.position

        return 0

    def _execute_signal(self, ctx, signal: SignalState, qty: int) -> None:
        if qty <= 0:
            return

        if signal == SignalState.BULLISH_CROSSOVER:
            ctx.buy(qty=qty)

        elif signal == SignalState.BEARISH_CROSSUNDER:
            ctx.sell(qty=qty)

    def _emit_diagnostics(
        self,
        ctx,
        signal: SignalState,
        snapshot: Optional[IndicatorSnapshot],
        reason: str,
    ) -> None:
        if not self.params.enable_diagnostics:
            return

        if snapshot is None:
            ctx.log(
                signal=signal.name,
                reason=reason,
                short_period=self.params.short_period,
                long_period=self.params.long_period,
            )
            return

        ctx.log(
            signal=signal.name,
            reason=reason,
            short_period=self.params.short_period,
            long_period=self.params.long_period,
            short_ema_prev=snapshot.short_ema_prev,
            short_ema_curr=snapshot.short_ema_curr,
            long_ema_prev=snapshot.long_ema_prev,
            long_ema_curr=snapshot.long_ema_curr,
            spread_prev=snapshot.spread_prev,
            spread_curr=snapshot.spread_curr,
            current_position=ctx.position,
            target_capacity=ctx.max_position,
        )`;

const SENTIMENT_CODE = `from dataclasses import dataclass, field
from enum import Enum, auto
from typing import List, Dict, Any

from kquant import Strategy, indicators
from kquant.nlp import SentimentAnalyzer
from kquant.feeds import RSSFeed


class TechnicalSignal(Enum):
    BULLISH_CROSSOVER = auto()
    BEARISH_CROSSUNDER = auto()
    NEUTRAL = auto()


class PositionDirective(Enum):
    ACCUMULATE = auto()
    REDUCE = auto()
    HOLD = auto()


@dataclass
class StrategyParameters:
    short_period: int = 10
    long_period: int = 30
    sentiment_weight: float = 0.35
    signal_threshold: float = 0.20
    articles_per_feed: int = 5
    enforce_warmup_guard: bool = True
    enable_diagnostics: bool = True


@dataclass
class TechnicalSnapshot:
    short_ema_prev: float
    short_ema_curr: float
    long_ema_prev: float
    long_ema_curr: float

    @property
    def spread_prev(self) -> float:
        return self.short_ema_prev - self.long_ema_prev

    @property
    def spread_curr(self) -> float:
        return self.short_ema_curr - self.long_ema_curr


@dataclass
class SentimentSnapshot:
    articles: List[Any] = field(default_factory=list)
    article_count: int = 0
    score: float = 0.0
    feed_breakdown: Dict[str, int] = field(default_factory=dict)


@dataclass
class CompositeSignalSnapshot:
    technical_state: TechnicalSignal
    technical_score: float
    sentiment_score: float
    blended_score: float
    directive: PositionDirective


class FeedAggregationService:
    """
    Encapsulates collection of RSS content so the strategy logic remains
    focused on signal construction rather than I/O orchestration.
    """

    def __init__(self, feeds: List[RSSFeed], articles_per_feed: int):
        self.feeds = feeds
        self.articles_per_feed = articles_per_feed

    def collect_latest_articles(self) -> SentimentSnapshot:
        aggregated_articles: List[Any] = []
        feed_breakdown: Dict[str, int] = {}

        for feed in self.feeds:
            batch = feed.fetch_latest(n=self.articles_per_feed)
            batch = batch or []
            aggregated_articles.extend(batch)
            feed_breakdown[str(feed)] = len(batch)

        return SentimentSnapshot(
            articles=aggregated_articles,
            article_count=len(aggregated_articles),
            feed_breakdown=feed_breakdown,
        )


class SignalFusionEngine:
    """
    Converts raw technical and sentiment inputs into a final execution directive.
    """

    def __init__(self, sentiment_weight: float, signal_threshold: float):
        self.sentiment_weight = sentiment_weight
        self.signal_threshold = signal_threshold

    def resolve_technical_state(self, snapshot: TechnicalSnapshot) -> TechnicalSignal:
        bullish_transition = (
            snapshot.short_ema_curr > snapshot.long_ema_curr
            and snapshot.short_ema_prev <= snapshot.long_ema_prev
        )

        bearish_transition = (
            snapshot.short_ema_curr < snapshot.long_ema_curr
            and snapshot.short_ema_prev >= snapshot.long_ema_prev
        )

        if bullish_transition:
            return TechnicalSignal.BULLISH_CROSSOVER
        if bearish_transition:
            return TechnicalSignal.BEARISH_CROSSUNDER
        return TechnicalSignal.NEUTRAL

    def map_technical_state_to_score(self, state: TechnicalSignal) -> float:
        if state == TechnicalSignal.BULLISH_CROSSOVER:
            return 1.0
        if state == TechnicalSignal.BEARISH_CROSSUNDER:
            return -1.0
        return 0.0

    def blend(self, technical_score: float, sentiment_score: float) -> float:
        technical_weight = 1.0 - self.sentiment_weight
        return technical_weight * technical_score + self.sentiment_weight * sentiment_score

    def resolve_directive(self, blended_score: float) -> PositionDirective:
        if blended_score > self.signal_threshold:
            return PositionDirective.ACCUMULATE
        if blended_score < -self.signal_threshold:
            return PositionDirective.REDUCE
        return PositionDirective.HOLD

    def build_composite_snapshot(
        self,
        technical_snapshot: TechnicalSnapshot,
        sentiment_score: float,
    ) -> CompositeSignalSnapshot:
        technical_state = self.resolve_technical_state(technical_snapshot)
        technical_score = self.map_technical_state_to_score(technical_state)
        blended_score = self.blend(technical_score, sentiment_score)
        directive = self.resolve_directive(blended_score)

        return CompositeSignalSnapshot(
            technical_state=technical_state,
            technical_score=technical_score,
            sentiment_score=sentiment_score,
            blended_score=blended_score,
            directive=directive,
        )


class SentimentDualMAStrategy(Strategy):
    """
    Multi-Factor Trend/Sentiment Allocation Strategy
    ------------------------------------------------
    Framework:
        1. Derive a directional technical impulse from dual-EMA transition behavior.
        2. Aggregate cross-source market news flow from multiple RSS endpoints.
        3. Infer a normalized sentiment factor using an NLP model.
        4. Fuse technical and textual signals into a single composite score.
        5. Translate the composite score into a position directive.
    """

    short_period: int = 10
    long_period: int = 30
    sentiment_weight: float = 0.35

    feeds = [
        RSSFeed("https://feeds.reuters.com/reuters/businessNews"),
        RSSFeed("https://feeds.bloomberg.com/markets/news.rss"),
        RSSFeed("https://rss.cnn.com/rss/money_latest.rss"),
    ]

    def setup(self):
        self.params = StrategyParameters(
            short_period=self.short_period,
            long_period=self.long_period,
            sentiment_weight=self.sentiment_weight,
        )

        self.nlp = SentimentAnalyzer(model="finbert-tone")
        self.feed_service = FeedAggregationService(
            feeds=self.feeds,
            articles_per_feed=self.params.articles_per_feed,
        )
        self.signal_engine = SignalFusionEngine(
            sentiment_weight=self.params.sentiment_weight,
            signal_threshold=self.params.signal_threshold,
        )

    def on_bar(self, ctx):
        self._validate_parameters()

        if not self._has_sufficient_history(ctx):
            self._emit_warmup_log(ctx)
            return

        technical_snapshot = self._build_technical_snapshot(ctx)
        sentiment_snapshot = self._build_sentiment_snapshot()
        composite_snapshot = self.signal_engine.build_composite_snapshot(
            technical_snapshot=technical_snapshot,
            sentiment_score=sentiment_snapshot.score,
        )

        self._execute_directive(ctx, composite_snapshot.directive)
        self._emit_diagnostics(
            ctx=ctx,
            technical_snapshot=technical_snapshot,
            sentiment_snapshot=sentiment_snapshot,
            composite_snapshot=composite_snapshot,
        )

    def _validate_parameters(self) -> None:
        if self.params.short_period <= 0 or self.params.long_period <= 0:
            raise ValueError("EMA periods must be positive")
        if self.params.short_period >= self.params.long_period:
            raise ValueError("short_period must be strictly less than long_period")
        if not 0.0 <= self.params.sentiment_weight <= 1.0:
            raise ValueError("sentiment_weight must be within [0, 1]")
        if self.params.articles_per_feed <= 0:
            raise ValueError("articles_per_feed must be positive")

    def _has_sufficient_history(self, ctx) -> bool:
        if not self.params.enforce_warmup_guard:
            return True
        required_bars = max(self.params.short_period, self.params.long_period) + 2
        return len(ctx.close) >= required_bars

    def _build_technical_snapshot(self, ctx) -> TechnicalSnapshot:
        close = ctx.close

        short_ema_series = indicators.ema(close, self.params.short_period)
        long_ema_series = indicators.ema(close, self.params.long_period)

        return TechnicalSnapshot(
            short_ema_prev=short_ema_series[-2],
            short_ema_curr=short_ema_series[-1],
            long_ema_prev=long_ema_series[-2],
            long_ema_curr=long_ema_series[-1],
        )

    def _build_sentiment_snapshot(self) -> SentimentSnapshot:
        snapshot = self.feed_service.collect_latest_articles()

        if snapshot.article_count == 0:
            snapshot.score = 0.0
            return snapshot

        snapshot.score = self.nlp.score(snapshot.articles)
        return snapshot

    def _execute_directive(self, ctx, directive: PositionDirective) -> None:
        if directive == PositionDirective.ACCUMULATE:
            ctx.buy(qty=ctx.max_position)
        elif directive == PositionDirective.REDUCE:
            ctx.sell(qty=ctx.position)

    def _emit_warmup_log(self, ctx) -> None:
        if not self.params.enable_diagnostics:
            return

        ctx.log(
            state="warmup",
            short_period=self.params.short_period,
            long_period=self.params.long_period,
        )

    def _emit_diagnostics(
        self,
        ctx,
        technical_snapshot: TechnicalSnapshot,
        sentiment_snapshot: SentimentSnapshot,
        composite_snapshot: CompositeSignalSnapshot,
    ) -> None:
        if not self.params.enable_diagnostics:
            return

        ctx.log(
            strategy="SentimentDualMAStrategy",
            technical_state=composite_snapshot.technical_state.name,
            execution_directive=composite_snapshot.directive.name,
            short_period=self.params.short_period,
            long_period=self.params.long_period,
            sentiment_weight=self.params.sentiment_weight,
            signal_threshold=self.params.signal_threshold,
            short_ema_prev=technical_snapshot.short_ema_prev,
            short_ema_curr=technical_snapshot.short_ema_curr,
            long_ema_prev=technical_snapshot.long_ema_prev,
            long_ema_curr=technical_snapshot.long_ema_curr,
            ema_spread_prev=technical_snapshot.spread_prev,
            ema_spread_curr=technical_snapshot.spread_curr,
            technical_score=composite_snapshot.technical_score,
            sentiment_score=composite_snapshot.sentiment_score,
            blended_score=composite_snapshot.blended_score,
            article_count=sentiment_snapshot.article_count,
            feed_breakdown=sentiment_snapshot.feed_breakdown,
            current_position=ctx.position,
            max_position=ctx.max_position,
        )`;

const AI_TRADING_CODE = `from dataclasses import dataclass
from enum import Enum, auto
from typing import Any, Dict, Optional

import schedule

from kquant import Strategy, indicators
from kquant.ai import ChatModel, OpenAIModel
from kquant.data import SentimentFeed, AccountInfo
from kquant.execution import TradeExecutor


class ProcessingStage(Enum):
    INITIALIZED = auto()
    DATA_COLLECTED = auto()
    TECHNICALS_COMPUTED = auto()
    SENTIMENT_EVALUATED = auto()
    STATE_NORMALIZED = auto()
    MARKET_ANALYZED = auto()
    DECISION_RESOLVED = auto()
    EXECUTION_DISPATCHED = auto()


class TradeAction(Enum):
    LONG_OPEN = "long_open"
    SHORT_OPEN = "short_open"
    FLAT_LONG = "flat_long"
    FLAT_SHORT = "flat_short"
    NO_ACTION = "no_action"


@dataclass
class ModelConfiguration:
    sentiment_model: str
    decision_model: str
    trade_model: str


@dataclass
class RuntimeComponents:
    sentiment_engine: ChatModel
    analysis_engine: ChatModel
    decision_engine: ChatModel
    sentiment_feed: SentimentFeed
    account_info: AccountInfo
    executor: TradeExecutor


@dataclass
class MarketContextSnapshot:
    kline_data: Any
    sentiment_score: Any
    account_state: Any


@dataclass
class EnrichedSignalPackage:
    technical_signals: Dict[str, Any]
    sentiment_analysis: Any
    account_state: Any

    def to_dict(self) -> Dict[str, Any]:
        return {
            "technical": self.technical_signals,
            "sentiment": self.sentiment_analysis,
            "account": self.account_state,
        }


@dataclass
class AnalysisArtifacts:
    normalized_input: Dict[str, Any]
    trade_analysis: Any
    decision: Dict[str, Any]


class AITradingStrategy(Strategy):
    """
    Multi-Layer AI-Augmented Tactical Allocation Strategy

    Architectural outline:
        1. Collect market, sentiment, and account state inputs
        2. Derive a multi-indicator technical feature set
        3. Run sentiment interpretation through an LLM-backed model
        4. Consolidate and normalize the analytical state
        5. Produce market analysis using a second LLM stage
        6. Resolve executable intent with a trade-decision model
        7. Dispatch the final action into the execution layer
    """

    schedule_interval: str = "15m"

    sentiment_model: str = "gpt-4o"
    decision_model: str = "gpt-4o"
    trade_model: str = "gpt-4o"

    def setup(self):
        self._stage: ProcessingStage = ProcessingStage.INITIALIZED
        self._config = self._build_model_configuration()
        self._runtime = self._initialize_runtime_components(self._config)

    def _build_model_configuration(self) -> ModelConfiguration:
        return ModelConfiguration(
            sentiment_model=self.sentiment_model,
            decision_model=self.decision_model,
            trade_model=self.trade_model,
        )

    def _initialize_runtime_components(
        self,
        config: ModelConfiguration,
    ) -> RuntimeComponents:
        sentiment_engine = ChatModel(model=OpenAIModel(config.sentiment_model))
        analysis_engine = ChatModel(model=OpenAIModel(config.decision_model))
        decision_engine = ChatModel(model=OpenAIModel(config.trade_model))

        return RuntimeComponents(
            sentiment_engine=sentiment_engine,
            analysis_engine=analysis_engine,
            decision_engine=decision_engine,
            sentiment_feed=SentimentFeed(source="multi"),
            account_info=AccountInfo(),
            executor=TradeExecutor(),
        )

    @schedule.every(15).minutes
    def on_tick(self, ctx):
        market_snapshot = self._collect_market_context(ctx)
        technical_state = self._compute_technical_state(market_snapshot)
        sentiment_state = self._evaluate_sentiment_state(market_snapshot)
        composite_state = self._compose_signal_package(
            technical_state=technical_state,
            sentiment_state=sentiment_state,
            account_state=market_snapshot.account_state,
        )
        artifacts = self._run_decision_pipeline(composite_state)
        self._dispatch_execution(artifacts.decision, ctx)
        self._emit_operational_log(ctx, market_snapshot, technical_state, artifacts)

    def _collect_market_context(self, ctx) -> MarketContextSnapshot:
        snapshot = MarketContextSnapshot(
            kline_data=ctx.get_kline(bars=50),
            sentiment_score=self._runtime.sentiment_feed.fetch(),
            account_state=self._runtime.account_info.query_positions(),
        )
        self._stage = ProcessingStage.DATA_COLLECTED
        return snapshot

    def _compute_technical_state(
        self,
        snapshot: MarketContextSnapshot,
    ) -> Dict[str, Any]:
        technicals = indicators.multi(
            snapshot.kline_data,
            features=["rsi", "macd", "bb", "ema_20", "ema_50"],
        )
        self._stage = ProcessingStage.TECHNICALS_COMPUTED
        return technicals

    def _evaluate_sentiment_state(
        self,
        snapshot: MarketContextSnapshot,
    ) -> Any:
        sentiment_analysis = self._runtime.sentiment_engine.analyze(
            sentiment_score=snapshot.sentiment_score,
            context="crypto market sentiment analysis",
        )
        self._stage = ProcessingStage.SENTIMENT_EVALUATED
        return sentiment_analysis

    def _compose_signal_package(
        self,
        technical_state: Dict[str, Any],
        sentiment_state: Any,
        account_state: Any,
    ) -> EnrichedSignalPackage:
        return EnrichedSignalPackage(
            technical_signals=technical_state,
            sentiment_analysis=sentiment_state,
            account_state=account_state,
        )

    def _run_decision_pipeline(
        self,
        package: EnrichedSignalPackage,
    ) -> AnalysisArtifacts:
        merged_state = package.to_dict()
        normalized_state = self._normalize_feature_space(merged_state)
        self._stage = ProcessingStage.STATE_NORMALIZED

        trade_analysis = self._runtime.analysis_engine.analyze(
            data=normalized_state,
            prompt="Analyze market conditions and suggest trading actions",
        )
        self._stage = ProcessingStage.MARKET_ANALYZED

        decision = self._runtime.decision_engine.decide(
            analysis=trade_analysis,
            account=package.account_state,
            risk_level="high",
        )
        self._stage = ProcessingStage.DECISION_RESOLVED

        return AnalysisArtifacts(
            normalized_input=normalized_state,
            trade_analysis=trade_analysis,
            decision=decision,
        )

    def _normalize_feature_space(self, payload: Dict[str, Any]) -> Dict[str, Any]:
        return {
            feature_namespace: self._scale_feature_value(feature_value)
            for feature_namespace, feature_value in payload.items()
        }

    def _scale_feature_value(self, value: Any) -> Any:
        if isinstance(value, (int, float)):
            return max(-1.0, min(1.0, float(value)))
        return value

    def _dispatch_execution(self, decision: Dict[str, Any], ctx) -> None:
        action = self._resolve_trade_action(decision)
        allocation_fraction = self._resolve_trade_size(decision)

        execution_quantity = allocation_fraction * ctx.max_position

        if action == TradeAction.LONG_OPEN:
            ctx.buy(qty=execution_quantity)

        elif action == TradeAction.SHORT_OPEN:
            ctx.sell_short(qty=execution_quantity)

        elif action == TradeAction.FLAT_LONG:
            ctx.close_long()

        elif action == TradeAction.FLAT_SHORT:
            ctx.close_short()

        self._stage = ProcessingStage.EXECUTION_DISPATCHED

    def _resolve_trade_action(self, decision: Dict[str, Any]) -> TradeAction:
        raw_action = decision.get("action", TradeAction.NO_ACTION.value)

        for candidate in TradeAction:
            if candidate.value == raw_action:
                return candidate

        return TradeAction.NO_ACTION

    def _resolve_trade_size(self, decision: Dict[str, Any]) -> float:
        raw_size = decision.get("size", 0.1)

        try:
            return float(raw_size)
        except (TypeError, ValueError):
            return 0.1

    def _emit_operational_log(
        self,
        ctx,
        market_snapshot: MarketContextSnapshot,
        technical_state: Dict[str, Any],
        artifacts: AnalysisArtifacts,
    ) -> None:
        ctx.log(
            stage=self._stage.name,
            action=artifacts.decision.get("action", "no_action"),
            decision=artifacts.decision,
            normalized_input=artifacts.normalized_input,
            technical_state=technical_state,
            account_state=market_snapshot.account_state,
        )`;

const AI_TRADING_SENTIMENT_CODE = `from dataclasses import dataclass
from enum import Enum, auto
from typing import Any, Dict, Optional

import schedule

from kquant import Strategy, indicators
from kquant.ai import ChatModel, OpenAIModel
from kquant.data import SentimentFeed, AccountInfo
from kquant.execution import TradeExecutor
from kquant.notifications import TelegramBot
from kquant.risk import StopLossManager


class StrategyLifecycleStage(Enum):
    INITIALIZED = auto()
    MARKET_DATA_COLLECTED = auto()
    TECHNICAL_STATE_COMPUTED = auto()
    SENTIMENT_STATE_COMPUTED = auto()
    FEATURE_SPACE_NORMALIZED = auto()
    MARKET_ANALYSIS_COMPLETED = auto()
    DECISION_RESOLVED = auto()
    RISK_OVERRIDE_APPLIED = auto()
    EXECUTION_COMPLETED = auto()
    NOTIFICATION_DISPATCHED = auto()


class TradeIntent(Enum):
    LONG_OPEN = "long_open"
    SHORT_OPEN = "short_open"
    FLAT_LONG = "flat_long"
    FLAT_SHORT = "flat_short"
    NO_ACTION = "no_action"


@dataclass
class ModelRegistry:
    sentiment_model: str
    analysis_model: str
    decision_model: str


@dataclass
class RiskConfiguration:
    stop_loss_pct: float
    take_profit_pct: float


@dataclass
class RuntimeServices:
    sentiment_engine: ChatModel
    analysis_engine: ChatModel
    decision_engine: ChatModel
    sentiment_feed: SentimentFeed
    account_provider: AccountInfo
    executor: TradeExecutor
    notifier: TelegramBot
    risk_manager: StopLossManager


@dataclass
class MarketInputSnapshot:
    kline_data: Any
    sentiment_score: Any
    account_state: Dict[str, Any]


@dataclass
class SignalEnvelope:
    technical_signals: Dict[str, Any]
    sentiment_analysis: Any
    account_state: Dict[str, Any]

    def as_payload(self) -> Dict[str, Any]:
        return {
            "technical": self.technical_signals,
            "sentiment": self.sentiment_analysis,
            "account": self.account_state,
        }


@dataclass
class DecisionArtifacts:
    normalized_payload: Dict[str, Any]
    market_analysis: Any
    decision: Dict[str, Any]


@dataclass
class ExecutionOutcome:
    action: TradeIntent
    size_fraction: float
    notification_message: str
    execution_performed: bool


class AITradingEnhancedStrategy(Strategy):
    """
    Multi-Layer AI Trading Strategy with Risk Override and Notification Routing

    Functional behavior:
        - Pull market, sentiment, and account state
        - Build technical indicators
        - Run sentiment inference
        - Normalize merged state
        - Run AI market analysis
        - Resolve AI trade decision
        - Apply stop-loss override when required
        - Execute action and publish Telegram notification
    """

    schedule_interval: str = "15m"

    sentiment_model: str = "gpt-4o"
    decision_model: str = "gpt-4o"
    trade_model: str = "gpt-4o"

    stop_loss_pct: float = 0.05
    take_profit_pct: float = 0.15

    def setup(self):
        self._stage = StrategyLifecycleStage.INITIALIZED
        self._models = self._build_model_registry()
        self._risk_config = self._build_risk_configuration()
        self._services = self._initialize_runtime_services(
            models=self._models,
            risk_config=self._risk_config,
        )

    def _build_model_registry(self) -> ModelRegistry:
        return ModelRegistry(
            sentiment_model=self.sentiment_model,
            analysis_model=self.decision_model,
            decision_model=self.trade_model,
        )

    def _build_risk_configuration(self) -> RiskConfiguration:
        return RiskConfiguration(
            stop_loss_pct=self.stop_loss_pct,
            take_profit_pct=self.take_profit_pct,
        )

    def _initialize_runtime_services(
        self,
        models: ModelRegistry,
        risk_config: RiskConfiguration,
    ) -> RuntimeServices:
        return RuntimeServices(
            sentiment_engine=ChatModel(model=OpenAIModel(models.sentiment_model)),
            analysis_engine=ChatModel(model=OpenAIModel(models.analysis_model)),
            decision_engine=ChatModel(model=OpenAIModel(models.decision_model)),
            sentiment_feed=SentimentFeed(source="multi"),
            account_provider=AccountInfo(),
            executor=TradeExecutor(),
            notifier=TelegramBot(
                token=self.config.get("TELEGRAM_TOKEN"),
                chat_id=self.config.get("TELEGRAM_CHAT_ID"),
            ),
            risk_manager=StopLossManager(
                stop_loss_pct=risk_config.stop_loss_pct,
                take_profit_pct=risk_config.take_profit_pct,
            ),
        )

    @schedule.every(15).minutes
    def on_tick(self, ctx):
        market_snapshot = self._collect_market_inputs(ctx)
        technical_state = self._compute_technical_feature_stack(market_snapshot)
        sentiment_state = self._compute_sentiment_state(market_snapshot)

        envelope = self._assemble_signal_envelope(
            technical_state=technical_state,
            sentiment_state=sentiment_state,
            account_state=market_snapshot.account_state,
        )

        artifacts = self._run_inference_pipeline(envelope)
        risk_adjusted_decision = self._apply_risk_management_overrides(
            decision=artifacts.decision,
            account_state=market_snapshot.account_state,
        )

        execution_outcome = self._execute_with_notification(
            decision=risk_adjusted_decision,
            ctx=ctx,
        )

        self._emit_strategy_log(
            ctx=ctx,
            market_snapshot=market_snapshot,
            technical_state=technical_state,
            artifacts=artifacts,
            execution_outcome=execution_outcome,
        )

    def _collect_market_inputs(self, ctx) -> MarketInputSnapshot:
        snapshot = MarketInputSnapshot(
            kline_data=ctx.get_kline(bars=50),
            sentiment_score=self._services.sentiment_feed.fetch(),
            account_state=self._services.account_provider.query_positions(),
        )
        self._stage = StrategyLifecycleStage.MARKET_DATA_COLLECTED
        return snapshot

    def _compute_technical_feature_stack(
        self,
        snapshot: MarketInputSnapshot,
    ) -> Dict[str, Any]:
        technicals = indicators.multi(
            snapshot.kline_data,
            features=["rsi", "macd", "bb", "ema_20", "ema_50"],
        )
        self._stage = StrategyLifecycleStage.TECHNICAL_STATE_COMPUTED
        return technicals

    def _compute_sentiment_state(
        self,
        snapshot: MarketInputSnapshot,
    ) -> Any:
        sentiment_analysis = self._services.sentiment_engine.analyze(
            sentiment_score=snapshot.sentiment_score,
            context="crypto market sentiment analysis",
        )
        self._stage = StrategyLifecycleStage.SENTIMENT_STATE_COMPUTED
        return sentiment_analysis

    def _assemble_signal_envelope(
        self,
        technical_state: Dict[str, Any],
        sentiment_state: Any,
        account_state: Dict[str, Any],
    ) -> SignalEnvelope:
        return SignalEnvelope(
            technical_signals=technical_state,
            sentiment_analysis=sentiment_state,
            account_state=account_state,
        )

    def _run_inference_pipeline(
        self,
        envelope: SignalEnvelope,
    ) -> DecisionArtifacts:
        merged_payload = envelope.as_payload()
        normalized_payload = self._normalize_feature_space(merged_payload)
        self._stage = StrategyLifecycleStage.FEATURE_SPACE_NORMALIZED

        market_analysis = self._services.analysis_engine.analyze(
            data=normalized_payload,
            prompt="Analyze market conditions and suggest trading actions",
        )
        self._stage = StrategyLifecycleStage.MARKET_ANALYSIS_COMPLETED

        decision = self._services.decision_engine.decide(
            analysis=market_analysis,
            account=envelope.account_state,
            risk_level="high",
        )
        self._stage = StrategyLifecycleStage.DECISION_RESOLVED

        return DecisionArtifacts(
            normalized_payload=normalized_payload,
            market_analysis=market_analysis,
            decision=decision,
        )

    def _apply_risk_management_overrides(
        self,
        decision: Dict[str, Any],
        account_state: Dict[str, Any],
    ) -> Dict[str, Any]:
        materialized_decision = dict(decision)

        if self._services.risk_manager.should_stop_loss(account_state):
            position = account_state.get("position", 0)

            materialized_decision["action"] = (
                TradeIntent.FLAT_LONG.value
                if position > 0
                else TradeIntent.FLAT_SHORT.value
            )

            self._services.notifier.send(
                "⚠️ Stop-loss triggered — flattening position"
            )
            self._stage = StrategyLifecycleStage.RISK_OVERRIDE_APPLIED

        return materialized_decision

    def _execute_with_notification(
        self,
        decision: Dict[str, Any],
        ctx,
    ) -> ExecutionOutcome:
        action = self._resolve_trade_intent(decision)
        size_fraction = self._resolve_position_fraction(decision)

        execution_qty = size_fraction * ctx.max_position
        execution_performed = False
        notification_message = ""

        if action == TradeIntent.LONG_OPEN:
            ctx.buy(qty=execution_qty)
            execution_performed = True
            notification_message = f"🟢 Long Open | Size: {size_fraction:.1%}"

        elif action == TradeIntent.SHORT_OPEN:
            ctx.sell_short(qty=execution_qty)
            execution_performed = True
            notification_message = f"🔴 Short Open | Size: {size_fraction:.1%}"

        elif action == TradeIntent.FLAT_LONG:
            ctx.close_long()
            execution_performed = True
            notification_message = "⬜ Flat Long"

        elif action == TradeIntent.FLAT_SHORT:
            ctx.close_short()
            execution_performed = True
            notification_message = "⬜ Flat Short"

        else:
            notification_message = "💤 No Action"

        self._stage = StrategyLifecycleStage.EXECUTION_COMPLETED

        if notification_message:
            self._services.notifier.send(notification_message)
            self._stage = StrategyLifecycleStage.NOTIFICATION_DISPATCHED

        return ExecutionOutcome(
            action=action,
            size_fraction=size_fraction,
            notification_message=notification_message,
            execution_performed=execution_performed,
        )

    def _normalize_feature_space(self, payload: Dict[str, Any]) -> Dict[str, Any]:
        return {
            namespace: self._scale_feature_value(value)
            for namespace, value in payload.items()
        }

    def _scale_feature_value(self, value: Any) -> Any:
        if isinstance(value, (int, float)):
            return max(-1.0, min(1.0, float(value)))
        return value

    def _resolve_trade_intent(self, decision: Dict[str, Any]) -> TradeIntent:
        raw_action = decision.get("action", TradeIntent.NO_ACTION.value)

        for intent in TradeIntent:
            if intent.value == raw_action:
                return intent

        return TradeIntent.NO_ACTION

    def _resolve_position_fraction(self, decision: Dict[str, Any]) -> float:
        raw_size = decision.get("size", 0.1)

        try:
            return float(raw_size)
        except (TypeError, ValueError):
            return 0.1

    def _emit_strategy_log(
        self,
        ctx,
        market_snapshot: MarketInputSnapshot,
        technical_state: Dict[str, Any],
        artifacts: DecisionArtifacts,
        execution_outcome: ExecutionOutcome,
    ) -> None:
        ctx.log(
            lifecycle_stage=self._stage.name,
            action=execution_outcome.action.value,
            decision=artifacts.decision,
            normalized_payload=artifacts.normalized_payload,
            market_analysis=artifacts.market_analysis,
            technical_state=technical_state,
            account_state=market_snapshot.account_state,
            execution_performed=execution_outcome.execution_performed,
            notification_message=execution_outcome.notification_message,
            stop_loss_pct=self._risk_config.stop_loss_pct,
            take_profit_pct=self._risk_config.take_profit_pct,
        )`;

// Light-theme syntax highlight colors
const HIGHLIGHT_REGEX =
  /(".*?"|'.*?')|(#.*)|("{3}[\s\S]*?"{3})|(\b(?:from|import|class|def|if|elif|else|return|and|or|not|self|int|float|str|True|False|None)\b)|(\b(?:Strategy|DualMovingAverageStrategy|SentimentAnalyzer|RSSFeed|SentimentDualMAStrategy|AITradingStrategy|AITradingEnhancedStrategy|ChatModel|OpenAIModel|SentimentFeed|AccountInfo|TradeExecutor|TelegramBot|StopLossManager)\b)|(\b(?:on_bar|on_tick|setup|fetch_latest|score|ema|buy|sell|sell_short|close_long|close_short|log|analyze|decide|multi|send|query_positions|fetch|should_stop_loss)\b)|(\b\d+\.?\d*\b)/g;

function highlightLine(line: string): string {
  if (line.trim() === "") return "&nbsp;";
  return line.replace(
    HIGHLIGHT_REGEX,
    (match, pString, pComment, _pDocstring, pKeyword, pClass, pFunc, pNum) => {
      if (pString) return `<span style="color:#c7254e">${pString}</span>`;
      if (pComment) return `<span style="color:#6a737d;font-style:italic">${pComment}</span>`;
      if (pKeyword) return `<span style="color:#0550ae;font-weight:600">${pKeyword}</span>`;
      if (pClass) return `<span style="color:#116329">${pClass}</span>`;
      if (pFunc) return `<span style="color:#8250df">${pFunc}</span>`;
      if (pNum) return `<span style="color:#0550ae">${pNum}</span>`;
      return match;
    }
  );
}

function highlightLines(rawLines: string[]): string[] {
  let inDocstring = false;
  return rawLines.map((line) => {
    const count = (line.match(/"""/g) || []).length;
    if (count > 0) {
      // toggle once per pair of """ on the same line; odd count flips state
      const wasIn = inDocstring;
      if (count % 2 !== 0) inDocstring = !inDocstring;
      if (wasIn || count % 2 !== 0) {
        return `<span style="color:#032f62;font-style:italic">${line}</span>`;
      }
    }
    if (inDocstring) {
      return `<span style="color:#032f62;font-style:italic">${line}</span>`;
    }
    return highlightLine(line);
  });
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path fillRule="evenodd" d="M9 4.5a.75.75 0 01.721.544l.813 2.846a3.75 3.75 0 002.576 2.576l2.846.813a.75.75 0 010 1.442l-2.846.813a3.75 3.75 0 00-2.576 2.576l-.813 2.846a.75.75 0 01-1.442 0l-.813-2.846a3.75 3.75 0 00-2.576-2.576l-2.846-.813a.75.75 0 010-1.442l2.846-.813A3.75 3.75 0 007.466 7.89l.813-2.846A.75.75 0 019 4.5zM18 1.5a.75.75 0 01.728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 010 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 01-1.456 0l-.258-1.036a2.625 2.625 0 00-1.91-1.91l-1.036-.258a.75.75 0 010-1.456l1.036-.258a2.625 2.625 0 001.91-1.91l.258-1.036A.75.75 0 0118 1.5zM16.5 15a.75.75 0 01.712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 010 1.422l-1.183.395c-.447.15-.799.5-.948.948l-.395 1.183a.75.75 0 01-1.422 0l-.395-1.183a1.5 1.5 0 00-.948-.948l-1.183-.395a.75.75 0 010-1.422l1.183-.395c.447-.15.799-.5.948-.948l.395-1.183A.75.75 0 0116.5 15z" clipRule="evenodd" />
    </svg>
  );
}

function getScenarioLabel(scenario: Props["scenario"]): string {
  switch (scenario) {
    case "baseline": return "Dual MA";
    case "sentiment": return "Dual MA + Sentiment";
    case "ai_trading": return "AI Trading";
    case "ai_trading_sentiment": return "AI Trading + Enhanced";
  }
}

// 2s blank pause, then lines revealed at 50ms/line
const INITIAL_PAUSE_MS = 2000;
const LINE_INTERVAL_MS = 5;

export function StrategyCodeView({ scenario, currentFlowStep, onRevealComplete, skipAnimation }: Props) {
  const currentCode =
    scenario === "baseline" ? BASELINE_CODE
      : scenario === "sentiment" ? SENTIMENT_CODE
        : scenario === "ai_trading" ? AI_TRADING_CODE
          : AI_TRADING_SENTIMENT_CODE;

  const rawLines = currentCode.trim().split("\n");
  const processedLines = highlightLines(rawLines);
  const totalLines = processedLines.length;

  // visibleLines: how many lines are currently shown (0 = blank waiting phase)
  const [visibleLines, setVisibleLines] = useState(() => skipAnimation ? totalLines : 0);
  const onRevealCompleteRef = useRef(onRevealComplete);
  useEffect(() => { onRevealCompleteRef.current = onRevealComplete; }, [onRevealComplete]);

  // Re-run animation when scenario/step changes
  const animKey = `${scenario}-${currentFlowStep}`;
  const animKeyRef = useRef(animKey);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    animKeyRef.current = animKey;

    // If skip requested (user manually switched back to this tab), show all immediately
    if (skipAnimation) {
      setVisibleLines(totalLines);
      return;
    }

    setVisibleLines(0);

    // Phase 1: 2s blank pause
    const pauseTimer = setTimeout(() => {
      if (animKeyRef.current !== animKey) return;

      // Phase 2: reveal lines one by one
      let revealed = 0;
      const interval = setInterval(() => {
        if (animKeyRef.current !== animKey) {
          clearInterval(interval);
          return;
        }
        revealed += 1;
        setVisibleLines(revealed);
        if (revealed >= totalLines) {
          clearInterval(interval);
          // Notify parent that reveal is done
          onRevealCompleteRef.current?.();
        }
      }, LINE_INTERVAL_MS);

      return () => clearInterval(interval);
    }, INITIAL_PAUSE_MS);

    return () => clearTimeout(pauseTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animKey, totalLines, skipAnimation]);

  // Auto-scroll to bottom as lines are revealed
  useEffect(() => {
    if (skipAnimation) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [visibleLines, skipAnimation]);

  const isGenerating = visibleLines < totalLines;
  const statusLabel = visibleLines === 0
    ? "Generating..."
    : isGenerating
      ? `Generating... (${visibleLines}/${totalLines})`
      : "Ready for backtesting";

  return (
    <div className="flex flex-col w-full h-full bg-[#f6f8fa] overflow-hidden">
      <style>{`
        @keyframes codeLineFadeIn {
          from { opacity: 0; transform: translateX(-6px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .code-line-reveal {
          animation: codeLineFadeIn 0.18s ease-out forwards;
        }
        .code-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .code-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .code-scrollbar::-webkit-scrollbar-thumb { background: #d0d7de; border-radius: 4px; }
        .code-scrollbar::-webkit-scrollbar-thumb:hover { background: #afb8c1; }
      `}</style>

      {/* ── Header bar ─────────────────────────────────────────── */}
      <div className="shrink-0 flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-white">
        {/* Left: file badge */}
        <div className="flex items-center gap-2">
          <span className="text-base leading-none">🐍</span>
          <span className="font-mono text-[13px] font-semibold text-gray-700">strategy.py</span>
          <span className="ml-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-500 border border-gray-200">
            {getScenarioLabel(scenario)}
          </span>
        </div>

        {/* Right: status */}
        <div className="flex items-center gap-1.5 text-[12px] font-medium text-emerald-600">
          <SparklesIcon className="w-3.5 h-3.5 shrink-0" />
          <span>{statusLabel}</span>
          {isGenerating && (
            <span className="inline-block w-1.5 h-3.5 bg-emerald-500 rounded-sm animate-pulse ml-0.5" />
          )}
        </div>
      </div>

      {/* ── Code area ──────────────────────────────────────────── */}
      <div ref={scrollRef} className="flex-1 overflow-auto code-scrollbar bg-white">
        <div className="flex min-w-max py-4">

          {/* Line numbers */}
          <div
            className="shrink-0 select-none text-right font-mono text-[13px] leading-[1.6] text-gray-400 px-4 border-r border-gray-100 sticky left-0 bg-white z-10"
            style={{ minWidth: "3rem" }}
          >
            {processedLines.slice(0, visibleLines).map((_, i) => (
              <div key={i}>
                {i + 1}
              </div>
            ))}
          </div>

          {/* Code lines */}
          <div className="pl-5 pr-8 font-mono text-[13px] leading-[1.6] text-gray-800">
            {processedLines.slice(0, visibleLines).map((lineHtml, i) => (
              <div
                key={`${animKey}-${i}`}
                className={skipAnimation ? "whitespace-pre" : "code-line-reveal whitespace-pre"}
                dangerouslySetInnerHTML={{ __html: lineHtml }}
              />
            ))}
          </div>

        </div>
      </div>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <div className="shrink-0 flex items-center justify-between px-4 py-1.5 border-t border-gray-200 bg-[#f6f8fa] text-[11px] text-gray-500 font-sans select-none">
        <span>Generated by KQuant AI</span>
        <div className="flex items-center gap-3">
          <span>Ln {totalLines}</span>
          <span>UTF-8</span>
          <span className="font-medium text-gray-600">Python</span>
        </div>
      </div>
    </div>
  );
}
